import React, { FunctionComponent, useState } from "react";
import { Header2 } from "@iventis/styles/src/components/texts";
import { Theme } from "@emotion/react";
import { useTheme } from "@mui/material";
import { StyledColumnFlex } from "@iventis/styles/src/components/forms";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { IventisFooter, SiteLogoComponent } from "@iventis/components";
import { LoginPanelContainer } from "@iventis/components/src/login-containers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { permissionsApi } from "@iventis/api/src/api";
import { TypographyColorsEnum, styled } from "@iventis/styles";
import { queryParams } from "@iventis/utilities";

type VerifyEmailPendingProps = {
    emailTimeout: number;
};

const VerifyEmailPending: FunctionComponent<VerifyEmailPendingProps> = ({ emailTimeout = 60 }) => {
    const { email } = queryParams;
    const translate = useIventisTranslate();
    const [timeLeft, setTimeLeft] = useState(null);
    const [targetTime, setTargetTime] = useState(null);
    const [timerTriggered, setTimerTriggered] = useState(false);
    const theme = useTheme<Theme>();

    const handleResetClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        triggerTimer();
        await permissionsApi.post<{ email: string }>(`email-verification-token/${email}`, undefined, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.login2.verification.ctas.resendEmail)?.toLocaleLowerCase?.() }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.login2.verification.ctas.resendEmail) }),
            },
        });
    };

    const triggerTimer = () => {
        setTargetTime(emailTimeout);
        setTimerTriggered(true);
        const finalTime = Number(new Date()) + emailTimeout * 1000;
        resendTimerInterval = setInterval(() => calculateTimeLeft(finalTime), 1000);
    };

    let resendTimerInterval;

    const calculateTimeLeft = (finalTime) => {
        const difference = finalTime - Number(new Date());
        if (difference >= 0) {
            setTimeLeft(Math.round(difference / 1000));
        } else {
            setTimeLeft(null);
            setTimerTriggered(false);
            clearInterval(resendTimerInterval);
        }
    };

    return (
        <ParentContainer>
            <LoginPanelContainer id="middle" style={{ textAlign: "center", gap: "5px" }}>
                <div style={{ marginBottom: "30px" }}>
                    <SiteLogoComponent />
                </div>

                <StyledColumnFlex>
                    <FontAwesomeIcon size="3x" icon={["fad", "envelope-dot"]} />
                </StyledColumnFlex>

                <StyledColumnFlex style={{ marginBottom: "30px" }}>
                    <Header2 style={{ margin: "10px 0 20px 0" }} color={TypographyColorsEnum.CORE}>
                        {translate(Content.login2.verification.headings.checkEmail)}
                    </Header2>

                    <p>
                        {translate(Content.login2.verification.emailSent)} <span style={{ display: "block", fontWeight: "bold", color: theme.primaryColors.focus }}>{email}</span>
                    </p>
                </StyledColumnFlex>
                <StyledColumnFlex>
                    <p style={{ fontSize: "14px" }}>
                        <span>{translate(Content.login2.verification.noEmail)}</span>
                        <span> </span>
                        <StyledLink
                            timerTriggered={timerTriggered}
                            onClick={!timerTriggered ? handleResetClick : (e) => e.preventDefault()}
                            href="/permissions/email-verification-token/{email}"
                        >
                            {translate(Content.login2.verification.ctas.resendEmail)}
                        </StyledLink>
                        {timerTriggered && <span style={{ display: "block" }}>{translate(Content.login2.verification.timerForResend, { time: timeLeft || targetTime })}</span>}
                    </p>
                </StyledColumnFlex>
            </LoginPanelContainer>
            <IventisFooter />
        </ParentContainer>
    );
};

const ParentContainer = styled.div`
    display: flex;
    flex-direction: column;
    place-items: center;
    min-height: 100vh;
    min-height: 100dvh;
`;

const StyledLink = styled.a<{ timerTriggered: boolean }>`
    text-decoration: underline;
    color: inherit;
    ${({ timerTriggered }) => (timerTriggered ? "opacity: 0.6; cursor: not-allowed" : "")};
`;

export default VerifyEmailPending;
